<template>
  <div>
    <div class="centered-header">
      <h2 class="ui header" style="color: #092442;">
        <i class="clipboard outline icon"></i>
        <div class="content">
          {{
            !isNaN(this.reservationId)
              ? 'Update Reservation'
              : 'New Reservation'
          }}
        </div>
      </h2>
    </div>
    <div class="ui divider"></div>

    <div class="ui fluid mini steps" style="width: 100%">
      <div
        class="step"
        :class="{ active: currentTab === 0 }"
        v-if="notFastReservation"
      >
        <i class="bed icon"></i>
        <div class="content">
          <div class="title">Patient Info</div>
          <div class="description">Enter patient information</div>
        </div>
      </div>

      <div class="step" :class="{ active: currentTab === 1 }" v-if="notUpdateReservation">
        <i class="clipboard list icon"></i>
        <div class="content">
          <div class="title">Reservation Info</div>
          <div class="description">Enter reservation information</div>
        </div>
      </div>

      <div class="step" :class="{ active: currentTab === 2 }">
        <i class="calendar alternate outline icon"></i>
        <div class="content">
          <div class="title">Packages & Visits Info</div>
          <div class="description">Enter packages & visits information</div>
        </div>
      </div>

      <div class="step" :class="{ active: currentTab === 3 }">
        <i class="info icon"></i>
        <div class="content">
          <div class="title">Confirm Data</div>
          <div class="description">Verify reservation type details</div>
        </div>
      </div>
    </div>

    <div class="ui grid" style="padding-top: 30px;">
      <patientInfoStep
        style="width: 100%"
        v-if="currentTab === 0 && notFastReservation"
        @reservation-patientStepChanges="patientEventListenner"
      />

      <reservationInfoStep
        @reservation-goBack="currentTab--"
        style="width: 100%"
        v-if="currentTab === 1"
        :canGoBack="notFastReservation"
        :doctor_id="reservationInfo.doctor ? reservationInfo.doctor.id : null"
        :reservationPackages="reservationPackages"
        :reservationId="reservationInfo.id"
        @reservation-reservationStepChanges="reservationInfoEventListenner"
        @reservation-reservationStepChanges-skipVisits="skipVisitsEventListenner"
      />

      <packagesVisits
        @reservation-goBack="currentTab--"
        style="width: 100%"
        v-if="currentTab === 2 && notUpdateReservation"
        :reservationPackages="reservationPackages"
        :reservationVisits="reservationVisits"
        @reservation-visitsInfo="visitsEventListener"
      />

      <reviewStep
        @reservation-goBack="currentTab = visitsSkipped ? 1 : 2"
        style="width: 100%"
        v-if="currentTab === 3"
        :reservationPackages="reservationPackages"
        :reservationVisits="reservationVisits"
        :patient="patientInfo"
        :doctor="doctor"
        :visitsSkipped="visitsSkipped"
        @reservation-reviewConfirmed="save"
      />
    </div>
  </div>
</template>

<script>
import PageHeader from '@/components/abstract_components/PageHeader';
import PatientStepInfo from '@/components/steps/reservations/new_reservations_steps/PatientInfoStep';
import ReservationInfoStep from '@/components/steps/reservations/new_reservations_steps/ReservationInfoStep';
import NewPackagesInfo from '@/components/steps/reservations/new_reservations_steps/NewPackagesStep';
import PackagesVists from '@/components/steps/reservations/new_reservations_steps/packages-visits-components/PackageVisits';
import ReviewStep from '@/components/steps/reservations/new_reservations_steps/ReviewStep';

const TABS = ['patientInfo', 'reservationInfo', 'packages & visits', 'review'];

export default {
  components: {
    pageHeader: PageHeader,
    patientInfoStep: PatientStepInfo,
    reservationInfoStep: ReservationInfoStep,
    newPackagesInfo: NewPackagesInfo,
    packagesVisits: PackagesVists,
    reviewStep: ReviewStep
  },
  data() {
    return {
      currentTab: 0,
      notFastReservation: false,
      notUpdateReservation: true,
      clinicId: undefined,
      reservationId: undefined,
      reservationInfo: {},
      reservationPackages: [],
      reservationVisits: [],
      responsePackage: [],
      doctor: undefined,
      patientInfo: undefined,
      visitsSkipped: false
    };
  },
  async created() {
    // Create new Reservation
    if (this.$route.name === 'New_Reservation') {
      this.clinicId = this.getFromLocalStorage('clinicId');

      this.reservationId = undefined;
      if (this.$route.query && this.$route.query.id) {
        this.setPatient(this.$route.query.id);
        this.notFastReservation = false;
        this.currentTab = 1;
      } else {
        // No patient is sent in the route
        this.notFastReservation = true;
        this.currentTab = 0;
      }
    } else {
      // Update existing reservation
      this.notUpdateReservation = false
      this.clinicId = this.getClinicId();
      this.reservationId = this.$route.params.id;
    }

    if (!isNaN(this.reservationId)) {
      // reservation update flow

      // get reservation
      const reservationResponse= await this.$http
        .get(`reservations/${this.reservationId}`, {
          params: {
            clinic_id: this.clinicId
          }
        })
        .then(res => res.data)
        .catch(error => {
          console.log('failed to get reservation', error);
          return undefined;
        });
      this.reservationInfo = reservationResponse.reservation
      const visits = reservationResponse.meta.visits
      this.responsePackage = reservationResponse.reservation.reservations_packages.map(rp => rp.package)
      // adjust reservationPackages
      if (this.reservationInfo) {
        this.reservationPackages = this.reservationInfo.reservations_packages.map(
          el => {
            let completePackage = this.getPackage(el.package_id)
            return ({
              id: completePackage.id,
              name: completePackage.name,
              quantity: el.repeated,
              units: completePackage.quantity, // units per package for cost later
              unit_price: completePackage.unit_price,
              unit_measurement_id: completePackage.unit_measurement_id,
              description: completePackage.description
            })
          }
        );

        this.reservationVisits = visits.map(visit => ({
          id: visit.id,
          time: visit.time,
          isNew: false,
          packages:
            visit.visit_details.map(v_details => ({
              id: v_details.package_id,
              quantity: v_details.quantity
            })) || []
        }));

        // set patient info for later step 4
        // this.patientInfo = this.reservationInfo.patient;
        this.setPatient(this.reservationInfo.patient_id);
      }

      this.currentTab = this.reservationInfo.patient && this.reservationInfo.patient.id ? 1 : 0;
    }
  },
  methods: {
    getPackage(packageId) {
      return this.responsePackage.find(selectedPackage => {
        return selectedPackage.id === packageId
      })
    },
    patientEventListenner(event) {
      this.patientInfo = event.data.patient;
      if (event.data.next) {
        this.currentTab = 1;
      }
    },
    reservationInfoEventListenner(event) {
      this.doctor = event.data.doctor;
      this.reservationPackages = event.data.reservationPackages;
      this.visitsSkipped = false
      this.currentTab = this.$route.name === 'Update_Reservation' ? 3 : 2;
    },
    skipVisitsEventListenner(event) {
      this.doctor = event.data.doctor;
      this.reservationPackages = event.data.reservationPackages;
      this.visitsSkipped = true
      this.currentTab = 3;
    },
    visitsEventListener(event) {
      this.reservationVisits = event.data.visits;
      this.currentTab++;
    },
    setPatient(id) {
      if (this.notUpdateReservation) {
        this.$http(`patients/${id}/data/basic`)
          .then(res => {
            if (res && res.data) this.patientInfo = res.data;
            return res.data;
          })
          .catch(error => {
            console.log('error fetching patient', error);
          })
      } else {
        this.patientInfo = this.reservationInfo.patient;
      }
    },
    filterVisitPackages(visistPackages) {
      return visistPackages
        .map(vp => {
          if (vp.quantity) {
            return {
              package_id: vp.id,
              quantity: vp.quantity
            };
          } else return undefined;
        })
        .filter(vp => vp !== undefined);
    },
    save(event) {
      const apiPackages = this.reservationPackages.map(p => ({
        package_id: p.id,
        repeated: p.quantity
      }));

      const apiVisits = this.reservationVisits.map(v => {
        const visitPackages = this.filterVisitPackages(v.packages);
        return {
          id: v.isNew ? null : v.id,
          time: v.time,
          packages: visitPackages
        };
      });

      let note = '';
      
      if (this.reservationInfo) {
        note = this.reservationInfo.note ? this.reservationInfo.note + '\n' : '';
      }

      const completedTransactions = event.data.transactions.filter(transaction => 
        !!transaction.package_id && transaction.amount > 0
      );
      
      const sendData = {
        doctor_id: this.doctor ? this.doctor.id : undefined,
        patient_id: this.patientInfo.id,
        package_ids: apiPackages,
        visits: apiVisits,
        patient: this.patientInfo,
        note: note + event.data.note,
        transactions: completedTransactions
      };

      if (!this.notFastReservation) {
        delete sendData.patient;
      }

      // update case put request
      if (this.reservationId !== undefined) {
        this.$http
          .put(`reservations/${this.reservationId}`, {
            clinic_id: this.clinicId,
            reservation: sendData
          })
          .then(response => {
            this.navigateTo('Home');
          });
      } else {
        // create new reservation case
        this.notFastReservation = !sendData.patient_id
        const newReservationURL = this.notFastReservation 
          ? 'reservations'
          : 'reservations/fast';

        this.$http
          .post(newReservationURL, {
            clinic_id: this.clinicId,
            reservation: sendData
          })
          .then(response => {
            this.navigateTo('Show_Reservation', {
              key: 'params',
              value: {
                id: response.data.id,
                clinic_id: this.clinicId
              }
            });
          });
      }
    }
  }
};
</script>

<style scoped>
.centered-header {
  display: flex;
  justify-content: center;
}
</style>



// WEBPACK FOOTER //
// src/components/reservations/ReservationForm.vue